import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import Grid from 'react-virtualized/dist/es/Grid'
import useInscribe from '@/utils/hooks/useInscribe'
import Button from '@/components/button-gradient-epic'
import GridItem from '../grid-item'
import InscriptionActionBuy from '@/components/inscription-actions/inscription-action-buy'
import { API_URL } from '@/utils/constants'
import axios from 'axios'
import InscriptionContent from '@/components/inscription-content'
import XIcon from '@/icons/x-icon'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import { NETWORK } from '@/utils/constants'

function shuffle(array: any) {
	let currentIndex = array.length,
		randomIndex

	// While there remain elements to shuffle.
	while (currentIndex > 0) {
		// Pick a remaining element.
		randomIndex = Math.floor(Math.random() * currentIndex)
		currentIndex--

		// And swap it with the current element.
		;[array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]]
	}

	return array
}

const ROWS = 1000
const COLUMNS = 1000

const Grids: FC<{
	tab: string
	height: number
	idMap: any
	numMap: any
	myMap: any
	escrowMap: any
	escrows: any
	width: number
	rowIndex: number
	columnIndex: number
	slug: string
}> = (props): ReactElement => {
	const { numMap, idMap, myMap, escrows, escrowMap, width, height, tab, rowIndex, columnIndex } =
		props
	const { inscribeBulk } = useInscribe('files')
	const [selected, setSelected] = React.useState({})
	const [selectedEscrow, setSelectedEscrow] = React.useState<any>(null)
	const [selectedEscrowIndex, setSelectedEscrowIndex] = React.useState<any>(null)
	const [isMouseDown, setIsMouseDown] = React.useState(false)
	const ref = React.createRef<any>()

	React.useEffect(() => {
		const grid = document.getElementById('grid')

		const handleMouseDown = (_e: any) => setIsMouseDown(true)
		const handleMouseUp = (_e: any) => setIsMouseDown(false)

		;(grid as any).addEventListener('mousedown', handleMouseDown)
		;(grid as any).addEventListener('mouseup', handleMouseUp)

		return () => {
			;(grid as any).removeEventListener('mousedown', handleMouseDown)
			;(grid as any).removeEventListener('mouseup', handleMouseUp)
		}
	}, [])

	const cellRenderer = ({ columnIndex, key, rowIndex, style }: any) => {
		const index = rowIndex * ROWS + columnIndex

		if (index + 1 > height) {
			return <React.Fragment />
		}
		return (
			<GridItem
				key={key}
				tab={tab}
				style={style}
				escrowMap={escrowMap}
				numMap={numMap}
				selected={selected}
				slug={props.slug}
				isMouseDown={isMouseDown}
				myMap={myMap}
				index={index}
				idMap={idMap}
				selectedEscrowIndex={selectedEscrowIndex}
				setSelected={(args: any) => {
					setSelectedEscrow(null)
					setSelectedEscrowIndex(null)
					setSelected(args)
				}}
				setSelectedEscrow={async (index: any) => {
					setSelected({})
					if (index === selectedEscrowIndex) {
						setSelectedEscrow(null)
						setSelectedEscrowIndex(null)
						return
					}

					setSelectedEscrow(null)
					setSelectedEscrowIndex(index)

					let id = numMap[index]
					let escrow = escrows.find((e: any) => e.inscription_id.startsWith(id))

					const { data: inscription } = await axios.get(
						`${API_URL}/inscription/${escrow.inscription_id}`
					)

					setSelectedEscrow({ ...inscription, escrow })
				}}
			/>
		)
	}

	React.useEffect(() => {
		if (ref.current) {
			ref.current.scrollToCell({ columnIndex, rowIndex })
		}
	}, [rowIndex, columnIndex, ref])

	const selectedItems = React.useMemo(() => {
		return Object.keys(selected).filter((e) => (selected as any)[e])
	}, [selected])
	const selectedEscrowPrice = useSatoshiPrice((selectedEscrow as any)?.escrow?.satoshi_price ?? 0)

	const onRemove = () => {
		setSelectedEscrow(null)
		setSelectedEscrowIndex(null)
		setSelected({})
	}

	const handleSelectRandom = () => {
		const values = shuffle(
			new Array(height)
				.fill('')
				.map((e, i) => i)
				.filter((e) => !numMap[e])
		)
			.slice(0, 10)
			.reduce((a: any, e: any) => Object.assign(a, { [e]: true }), {})

		setSelected((prev) => ({ ...prev, ...values }))
	}

	return (
		<div className="relative">
			{selectedEscrow && !selectedItems?.length && (
				<div className="fixed bottom-[100px] left-0 w-full flex justify-center z-[1]">
					<div className="bg-zinc-900 flex flex-col p-4 rounded-xl w-[343px] gap-5">
						<div className="p-3 flex justify-between items-center">
							<div className="justify-between items-center flex gap-5">
								<InscriptionContent
									className="size-[46px]"
									inscription={selectedEscrow}
									smallCard={true}
								/>
								<div className="flex gap-1 flex-col">
									<div className={classNames('text-md', 'semibold')}>
										{(selectedEscrow as any)?.meta?.name}
									</div>
									<div className={classNames('text-sm', 'medium', 'text-muted-foreground')}>
										{selectedEscrowPrice} {NETWORK}
									</div>
								</div>
							</div>
							<XIcon className="size-8 stroke-zinc-400 cursor-pointer" onClick={onRemove} />
						</div>
						<InscriptionActionBuy className="min-w-[200px] w-full" inscription={selectedEscrow} />
					</div>
				</div>
			)}
			{!!selectedItems?.length && (
				<div className="fixed bottom-[100px] left-0 w-full flex justify-center z-[1]">
					<div className="bg-zinc-900 p-4 rounded-xl w-[343px] max-w-full flex flex-col gap-3">
						<Button className="min-w-[200px] w-full" onClick={handleSelectRandom}>
							Add Random 10
						</Button>
						<Button
							className="min-w-[200px] w-full"
							onClick={() => {
								let value = {
									bitmap: '<BLK>.bitmap',
									natmap:
										'{"p": "tap","op": "dmt-mint","dep": "4d967af36dcacd7e6199c39bda855d7b1b37268f4c8031fed5403a99ac57fe67i0","tick": "nat","blk": "<BLK>"}'
								}[props.slug]

								inscribeBulk(
									selectedItems.map(
										(e) =>
											new File(
												[(value as any).replace('<BLK>', e)],
												(value as any).replace('<BLK>', e),
												{
													type: 'text/plain'
												}
											)
									)
								)
								setSelected({})
							}}
						>
							Inscribe {selectedItems?.length}
						</Button>
					</div>
				</div>
			)}
			{!selectedItems.length && (
				<div className="fixed bottom-[100px] left-0 w-full flex justify-center z-[1]">
					<div className="bg-zinc-900 p-4 rounded-xl w-[343px] max-w-full flex flex-col gap-3">
						<Button className="min-w-[200px] w-full" onClick={handleSelectRandom}>
							Select Random 10
						</Button>
					</div>
				</div>
			)}
			<Grid
				ref={ref}
				id="grid"
				width={width} // Width of the grid container
				height={width} // Height of the grid container
				columnCount={COLUMNS} // Number of columns
				rowCount={ROWS} // Number of rows
				columnWidth={20} // Width of each column
				rowHeight={20} // Height of each row
				scrollToAlignment="start"
				overscanRowCount={25} // Render extra rows outside viewport
				overscanColumnCount={25} // Render extra columns outside viewport
				cellRenderer={cellRenderer} // Cell renderer function
			/>
		</div>
	)
}

export default Grids
